import React, { useMemo } from "react";
import { Box, Divider, Link, Typography } from "@mui/material";
import { parseInline, parseMarkdown, safeHref } from "../utils/markdown";

const HEADING_STYLES = {
  1: { variant: "h4", sx: { fontWeight: 800, mt: 0, mb: 1.5, letterSpacing: "-0.02em" } },
  2: {
    variant: "h6",
    sx: {
      fontWeight: 800,
      mt: 4,
      mb: 1,
      textTransform: "uppercase",
      letterSpacing: 1,
      fontSize: "0.95rem",
      color: "#0a0a0a",
      pb: 0.75,
      borderBottom: "2px solid #0a0a0a",
    },
  },
  3: { variant: "subtitle1", sx: { fontWeight: 700, mt: 3, mb: 1 } },
};

const Inline = ({ text }) => (
  <>
    {parseInline(text).map((node, i) => {
      if (node.type === "strong") {
        return (
          <Box key={i} component="strong" sx={{ fontWeight: 700 }}>
            {node.text}
          </Box>
        );
      }
      if (node.type === "em") {
        return (
          <Box key={i} component="em" sx={{ fontStyle: "italic" }}>
            {node.text}
          </Box>
        );
      }
      if (node.type === "code") {
        return (
          <Box
            key={i}
            component="code"
            sx={{
              fontFamily: "monospace",
              backgroundColor: "#f3f3f3",
              borderRadius: 0.5,
              px: 0.5,
            }}
          >
            {node.text}
          </Box>
        );
      }
      if (node.type === "link") {
        const href = safeHref(node.href);
        if (!href) return <React.Fragment key={i}>{node.text}</React.Fragment>;
        const external = !href.startsWith("/");
        return (
          <Link
            key={i}
            href={href}
            {...(external ? { target: "_blank", rel: "noreferrer" } : {})}
            sx={{ color: "#0a0a0a", textDecorationColor: "#bbb" }}
          >
            {node.text}
          </Link>
        );
      }
      return <React.Fragment key={i}>{node.text}</React.Fragment>;
    })}
  </>
);

/**
 * Renders a league document written in the Markdown subset in `utils/markdown`.
 * `dense` uses smaller type and tighter spacing for cards.
 */
const Markdown = ({ source, sx, dense = false }) => {
  const blocks = useMemo(() => parseMarkdown(source), [source]);
  const bodyVariant = dense ? "body2" : "body1";

  return (
    <Box sx={{ color: "#1a1a1a", lineHeight: 1.65, ...sx }}>
      {blocks.map((block, index) => {
        if (block.type === "heading") {
          const style = HEADING_STYLES[block.level] || HEADING_STYLES[3];
          return (
            <Typography
              key={index}
              component={`h${Math.min(block.level + 1, 6)}`}
              variant={style.variant}
              sx={style.sx}
            >
              <Inline text={block.text} />
            </Typography>
          );
        }

        if (block.type === "rule") {
          return <Divider key={index} sx={{ my: 3 }} />;
        }

        if (block.type === "quote") {
          return (
            <Box
              key={index}
              component="blockquote"
              sx={{
                m: 0,
                mt: 3,
                pl: 2,
                borderLeft: "3px solid #ddd",
                color: "#555",
                fontStyle: "italic",
              }}
            >
              <Typography variant="body2">
                <Inline text={block.text} />
              </Typography>
            </Box>
          );
        }

        if (block.type === "list") {
          return (
            <Box
              key={index}
              component={block.ordered ? "ol" : "ul"}
              start={block.ordered ? block.start : undefined}
              sx={{
                pl: dense ? 2.25 : 3,
                my: dense ? 0.5 : 1,
                "& li": { mb: dense ? 0.4 : 1.25 },
                "& li::marker": { fontWeight: 700, color: "#0a0a0a" },
              }}
            >
              {block.items.map((item, i) => (
                <Typography key={i} component="li" variant={bodyVariant}>
                  <Inline text={item} />
                </Typography>
              ))}
            </Box>
          );
        }

        return (
          <Typography key={index} variant={bodyVariant} sx={{ my: dense ? 0.75 : 1.5 }}>
            <Inline text={block.text} />
          </Typography>
        );
      })}
    </Box>
  );
};

export default Markdown;
